import { useTranslation } from "@redotech/redo-i18n";
import { ReturnReasonMatchType } from "@redotech/redo-return-core-common/return-flow/condition-enums";
import type { ReturnReasonState } from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { memo, useContext, useMemo } from "react";
import { FlowOptionsContext } from "../condition";
import { getReasonOptionsForReturnReasonCondition } from "./return-reason-options";

function matchTypeOptions(
  t: (key: string) => string,
): RadioGroupOption<ReturnReasonMatchType>[] {
  return [
    { id: ReturnReasonMatchType.ANY, label: t("Any match") },
    { id: ReturnReasonMatchType.ALL, label: t("All match") },
    { id: ReturnReasonMatchType.NONE, label: t("None match") },
  ];
}

export const ReturnReasonDetails = memo(function ReturnReasonDetails({
  state,
  setState,
  disabled,
}: ConditionTypeDetailsProps<ReturnReasonState>) {
  const { t } = useTranslation();
  const flowOptions = useContext(FlowOptionsContext);

  const options: BadgeRedoListItem<string>[] = useMemo(() => {
    const reasonOptions = flowOptions
      ? getReasonOptionsForReturnReasonCondition({
          claimReasonOptions: flowOptions.claimReasonOptions,
          conditionName: flowOptions.returnReasonConditionName,
          returnReasonOptions: flowOptions.returnReasonOptions,
          warrantyReasonOptions: flowOptions.warrantyReasonOptions,
        })
      : [];
    return reasonOptions.map((reason) => ({
      type: "badge" as const,
      id: reason,
      value: reason,
      badge: { text: reason, color: "gray" as const },
    }));
  }, [flowOptions]);

  const selectedOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return state.reasons.map((reason) => ({
      type: "badge" as const,
      id: reason,
      value: reason,
      badge: { text: reason, color: "gray" as const },
    }));
  }, [state.reasons]);

  const label = flowOptions?.returnReasonConditionName ?? t("Return reason");
  const pluralLabel = flowOptions?.returnReasonConditionName
    ? flowOptions.returnReasonConditionName
    : t("Return reasons");

  return (
    <>
      {flowOptions?.showReasonMatchType && (
        <RedoRadioGroup
          disabled={disabled}
          options={matchTypeOptions(t)}
          setValue={(matchType) => setState({ ...state, matchType })}
          size="xs"
          value={state.matchType}
        />
      )}
      <RedoTagsDropdownInput
        description={t(
          "Any match applies if any returned item has a selected {{label}}; All if every item does; None if no item does.",
          { label: label.toLowerCase() },
        )}
        disabled={disabled}
        label={pluralLabel}
        options={options}
        placeholder={t("Select {{label}}...", {
          label: pluralLabel.toLowerCase(),
        })}
        selectedOptions={selectedOptions}
        setSelectedOptions={(items) => {
          setState({ ...state, reasons: items.map((item) => item.value) });
        }}
        size="xs"
      />
    </>
  );
});
